import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getMatchById } from '../services/MatchService';
import { Match } from '../models/Match';
import { mapMappings, divisionMappings } from '../Constants';
import styles from '../styles/Tables.module.css';
import buttonStyles from '../styles/MatchButton.module.css';
import { parseMatchTime } from '../utils/matchTime';

interface MatchTableProps {
  matches: Match[];
  compact: boolean;
  season?: number;
  predictMode?: boolean;
  highlightPlayers?: string[]; // New prop for highlighting specific players
  showDivision?: boolean; // Show division badge when viewing mixed divisions
}

const EMPTY_PLAYERS: string[] = [];

const MatchTable: React.FC<MatchTableProps> = ({
  matches,
  compact,
  season,
  predictMode = false,
  highlightPlayers = EMPTY_PLAYERS,
  showDivision = false
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [bo3Maps, setBo3Maps] = useState<{ [matchId: string]: string[] }>({});
  const location = useLocation();

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ascending' | 'descending';
  }>({
    key: 'date',
    direction: 'descending',
  });

  // Keep track of window resizing to handle mobile layout changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch maps for BO3 matches
  useEffect(() => {
    const fetchBo3Maps = async () => {
      const bo3Matches = matches.filter(m => m.bo3 && m.team1score + m.team2score > 0);
      
      for (const match of bo3Matches) {
        if (!bo3Maps[match.matchid]) {
          try {
            const matchDetails = await getMatchById(match.matchid);
            if (matchDetails && matchDetails.length > 0) {
              // Filter out the parent BO3 match and only get the individual maps
              const maps = matchDetails
                .filter((m: Match) => !m.bo3 && m.mapName)
                .map((m: Match) => m.mapName);
              
              if (maps.length > 0) {
                setBo3Maps(prev => ({ ...prev, [match.matchid]: maps }));
              }
            }
          } catch (error) {
            console.error(`Error fetching BO3 maps for match ${match.matchid}:`, error);
          }
        }
      }
    };

    fetchBo3Maps();
  }, [matches, bo3Maps]);

  // Sorting
  const handleSort = (key: string) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Sort the matches array using the chosen config
  const sortedMatches = useMemo(() => {
    const sorted = [...matches];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortConfig.key) {
          case 'date':
            aValue = parseMatchTime(a.match_time).getTime();
            bValue = parseMatchTime(b.match_time).getTime();
            break;
          case 'team1':
            aValue = a.team1?.teamName || '';
            bValue = b.team1?.teamName || '';
            break;
          case 'score':
            aValue = a.team1score - a.team2score; // net difference
            bValue = b.team1score - b.team2score;
            break;
          case 'team2':
            aValue = a.team2?.teamName || '';
            bValue = b.team2?.teamName || '';
            break;
          case 'map':
            aValue = a.mapName;
            bValue = b.mapName;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sorted;
  }, [matches, sortConfig]);

  /**
   * Helper: Build link for a team. If we have a `season`, append ?season=...
   */
  const buildTeamLink = (teamId: string) => {
    if (season !== undefined) {
      return `/team/${teamId}?season=${season}`;
    }
    return `/team/${teamId}`;
  };
  
  /**
   * Create match detail link with returnURL parameters
   */
  const buildMatchLink = (matchId: string) => {
    // Check if we're in the player page context by looking for steamId in the URL
    const currentPath = location.pathname;
    const pathParts = currentPath.split('/');
    const isPlayerPage = pathParts[1] === 'player' && pathParts.length >= 3;
    
    if (isPlayerPage) {
      const steamId = pathParts[2];
      // Get the current tab and season to return to
      const params = new URLSearchParams(location.search);
      const currentTab = params.get('tab') || 'matches'; // Default to matches tab
      const currentSeason = params.get('season') || season?.toString();
      
      // Build the return URL with proper tab and season
      let returnUrl = `/player/${steamId}?tab=${currentTab}`;
      if (currentSeason) {
        returnUrl += `&season=${currentSeason}`;
      }
      
      // Encode the return URL and add it to the match link
      return `/match/${matchId}?returnUrl=${encodeURIComponent(returnUrl)}`;
    }
    
    return `/match/${matchId}`;
  };

  // Helper function to check if a match contains any of the highlighted players
  const shouldHighlightMatch = (match: Match) => {
    if (!highlightPlayers || highlightPlayers.length === 0) return false;
    
    // Check team1 players
    const team1HasHighlightedPlayer = match.team1playerids?.some(playerId => 
      highlightPlayers.includes(playerId)
    );
    
    // Check team2 players
    const team2HasHighlightedPlayer = match.team2playerids?.some(playerId => 
      highlightPlayers.includes(playerId)
    );
    
    // Highlight if any team has a highlighted player
    return team1HasHighlightedPlayer || team2HasHighlightedPlayer;
  };

  // If no matches, show a message
  if (sortedMatches.length === 0) {
    return (
      <div className="container text-center">
        <h1>Ingen kampe fundet</h1>
      </div>
    );
  }

  // Get short division name for badge display
  const getDivisionBadge = (division: string) => {
    if (division === 'ligaen') return 'L';
    if (division === '1div') return '1D';
    if (division === '2divA') return '2A';
    if (division === '2divB') return '2B';
    if (division === '3divA') return '3A';
    if (division === '3divB') return '3B';
    if (division === '3divC') return '3C';
    if (division === '3divD') return '3D';
    return division?.substring(0, 2) || '?';
  };

  // Column configuration - balanced for proper centering
  const columnWidths = {
    date: '15%',
    team1: predictMode ? '35%' : (isMobile ? '30%' : '25%'),
    score: predictMode ? '30%' : (isMobile ? '40%' : '20%'),
    team2: predictMode ? '35%' : (isMobile ? '30%' : '25%'),
    map: '15%'
  };

  // Render the table
  return (
    <div className={`${styles.tableContainer} ${styles.dark}`}>
      <table className={`${styles.table} ${(isMobile || predictMode) ? styles.compact : ''}`} style={{ tableLayout: 'fixed', width: '100%' }}>
        <thead>
          <tr className={`${styles.tableHeader} ${styles.dark}`}>
            {/* Date column - hide in compact or predict mode */}
            {!isMobile && !compact && !predictMode && (
              <th
                className={`${styles.tableHeaderCell} ${styles.dark}`}
                style={{ width: columnWidths.date }}
                onClick={() => handleSort('date')}
              >
                Dato{" "}
                {sortConfig.key === 'date' &&
                  (sortConfig.direction === 'ascending' ? '▲' : '▼')}
              </th>
            )}
            <th
              className={`${styles.tableHeaderCell} ${styles.dark}`}
              style={{ width: columnWidths.team1 }}
              onClick={() => handleSort('team1')}
            >
              Hold 1{" "}
              {sortConfig.key === 'team1' &&
                (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </th>
            <th
              className={`${styles.tableHeaderCell} ${styles.center} ${styles.dark}`}
              style={{ width: columnWidths.score }}
              onClick={() => handleSort('score')}
            >
              Score{" "}
              {sortConfig.key === 'score' &&
                (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </th>
            <th
              className={`${styles.tableHeaderCell} ${styles.dark}`}
              style={{ width: columnWidths.team2 }}
              onClick={() => handleSort('team2')}
            >
              Hold 2{" "}
              {sortConfig.key === 'team2' &&
                (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </th>
            {/* Map column - hide in predict mode */}
            {!isMobile && !predictMode && (
              <th
                className={`${styles.tableHeaderCell} ${styles.dark}`}
                style={{ width: columnWidths.map }}
                onClick={() => handleSort('map')}
              >
                Map{" "}
                {sortConfig.key === 'map' &&
                  (sortConfig.direction === 'ascending' ? '▲' : '▼')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedMatches.map((match, index) => {
            // Check if this match should be highlighted
            const isHighlighted = shouldHighlightMatch(match);
            
            // Get highlighted player positions for this match
            const team1HighlightedPlayers = highlightPlayers ? 
              match.team1playerids?.filter(playerId => highlightPlayers.includes(playerId)) : [];
            const team2HighlightedPlayers = highlightPlayers ? 
              match.team2playerids?.filter(playerId => highlightPlayers.includes(playerId)) : [];
            
            return (
              <tr 
                key={match.matchid} 
                className={`
                  ${styles.tableRow} 
                  ${styles.standard}
                  ${styles.dark} 
                  ${isMobile ? styles.mobile : predictMode ? styles.compact : ''}
                  ${isHighlighted ? styles.highlighted : ''}
                `}
                style={{ cursor: predictMode ? 'pointer' : undefined }}
              >
                {/* Date/time (if not mobile & not compact & not predict mode) */}
                {!isMobile && !compact && !predictMode && (
                  <td className="align-middle text-center">
                    <div className="d-flex flex-column align-items-center">
                      {showDivision && match.division && (
                        <span
                          className="badge mb-1"
                          style={{
                            backgroundColor: match.division === 'ligaen' ? '#f15c25' : '#6c757d',
                            fontSize: '0.7rem',
                            padding: '2px 6px'
                          }}
                          title={divisionMappings.get(match.division) || match.division}
                        >
                          {getDivisionBadge(match.division)}
                        </span>
                      )}
                      <span>
                        {parseMatchTime(match.match_time).toLocaleDateString('da-DK', {
                          day: 'numeric',
                          month: 'long',
                        }) +
                          ', ' +
                          parseMatchTime(match.match_time).toLocaleTimeString('da-DK', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          })}
                      </span>
                    </div>
                  </td>
                )}

                {/* Team 1 link */}
                <td className="align-middle text-center" style={{ 
                  padding: isMobile || predictMode ? '0.5rem' : undefined,
                  width: columnWidths.team1
                }}>
                  <Link
                    to={buildTeamLink(match.team1.teamId)}
                    className={`text-decoration-none text-light d-flex flex-column align-items-center`}
                  >
                    <div style={{ 
                      width: isMobile ? 30 : predictMode ? 25 : 40, 
                      height: isMobile ? 30 : predictMode ? 25 : 40 
                    }}>
                      <img
                        src={(() => {
                          const logoUrl = match.team1?.teamLogo;
                          if (!logoUrl) return '';
                          // Determine the desired width based on display mode
                          const desiredWidth = isMobile ? 60 : predictMode ? 50 : 80;
                          // Add width parameter to URL if it doesn't already have one
                          if (logoUrl.includes('getTeamLogos.php') && !logoUrl.includes('width=')) {
                            return `${logoUrl}&width=${desiredWidth}`;
                          }
                          return logoUrl;
                        })()}
                        alt={match.team1?.teamName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                        loading="lazy"
                      />
                    </div>
                    <span className="mt-1 text-truncate" style={{
                      maxWidth: '100%',
                      // Add visual indicator if this team has highlighted players
                      fontWeight: team1HighlightedPlayers?.length ? 'bold' : undefined
                    }}>
                      {match.team1?.teamName}
                      {team1HighlightedPlayers?.length ? ' ★' : ''}
                    </span>
                  </Link>
                </td>

                {/* Score */}
                <td className="align-middle text-center" style={{ 
                  padding: isMobile || predictMode ? '0.5rem' : undefined,
                  width: columnWidths.score
                }}>
                  <div className={buttonStyles.scoreContainer}>
                    <div className={`${buttonStyles.scoreText} ${isMobile || predictMode ? buttonStyles.small : ''}`}>
                      <span className={match.team1won ? buttonStyles.scoreTeam2 : buttonStyles.scoreTeam1}>
                        {match.team1score}
                      </span>
                      <span className={buttonStyles.scoreDivider}>-</span>
                      <span className={match.team1won ? buttonStyles.scoreTeam1 : buttonStyles.scoreTeam2}>
                        {match.team2score}
                      </span>
                    </div>
                    {/* Match detail link with improved styling */}
                    <Link 
                      to={buildMatchLink(match.matchid)} 
                      className={`${buttonStyles.matchButton} ${isMobile ? buttonStyles.small : predictMode ? buttonStyles.compact : ''}`}
                    >
                      {isMobile || predictMode ? 'Se' : 'Se kamp'}
                    </Link>
                  </div>
                </td>

                {/* Team 2 link */}
                <td className="align-middle text-center" style={{ 
                  padding: isMobile || predictMode ? '0.5rem' : undefined,
                  width: columnWidths.team2
                }}>
                  <Link
                    to={buildTeamLink(match.team2.teamId)}
                    className={`text-decoration-none text-light d-flex flex-column align-items-center`}
                  >
                    <div style={{ 
                      width: isMobile ? 30 : predictMode ? 25 : 40, 
                      height: isMobile ? 30 : predictMode ? 25 : 40 
                    }}>
                      <img
                        src={(() => {
                          const logoUrl = match.team2?.teamLogo;
                          if (!logoUrl) return '';
                          // Determine the desired width based on display mode
                          const desiredWidth = isMobile ? 60 : predictMode ? 50 : 80;
                          // Add width parameter to URL if it doesn't already have one
                          if (logoUrl.includes('getTeamLogos.php') && !logoUrl.includes('width=')) {
                            return `${logoUrl}&width=${desiredWidth}`;
                          }
                          return logoUrl;
                        })()}
                        alt={match.team2?.teamName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                        loading="lazy"
                      />
                    </div>
                    <span className="mt-1 text-truncate" style={{
                      maxWidth: '100%',
                      // Add visual indicator if this team has highlighted players
                      fontWeight: team2HighlightedPlayers?.length ? 'bold' : undefined
                    }}>
                      {match.team2?.teamName}
                      {team2HighlightedPlayers?.length ? ' ★' : ''}
                    </span>
                  </Link>
                </td>

                {/* Map info (if not mobile and not predict mode) */}
                {!isMobile && !predictMode && (
                  <td className="align-middle text-center">
                    {match.bo3 ? (
                      bo3Maps[match.matchid] && bo3Maps[match.matchid].length > 0 ? (
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: '2px'
                        }}>
                          {bo3Maps[match.matchid].map((map: string, idx: number) => (
                            <span key={idx}>
                              {mapMappings.get(map) || map}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ 
                          color: '#ff9933',
                          fontWeight: '500'
                        }}>
                          BO3
                        </span>
                      )
                    ) : (
                      mapMappings.get(match.mapName) || match.mapName
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MatchTable;